import "../../../styles/Auth/report.css";
import {
    TextField,
    Button,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { getData } from "../../../webService/webservice";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
const ReportPage = ({ sessionToken }) => {

    const [reportSummary, setReportSummary] = useState(null);
    const [loading, setLoading] = useState(false);

    const [dateRange, setDateRange] = useState("Today");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [filterType, setFilterType] = useState("All");

    /** Convert dropdown selection to actual start & end date */
    const getDateRange = (range) => {
        const now = new Date();
        let start = null, end = null;

        switch (range) {
            case "Today":
                start = end = now;
                break;

            case "Yesterday":
                start = new Date(now);
                start.setDate(start.getDate() - 1);
                end = start;
                break;

            case "Last 7 Days":
                end = now;
                start = new Date(now);
                start.setDate(now.getDate() - 7);
                break;

            case "Current Month":
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;

            case "Last Month":
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                end = new Date(now.getFullYear(), now.getMonth(), 0);
                break;

            case "Current FY":
                start = new Date(now.getFullYear(), 3, 1);
                end = new Date(now.getFullYear() + 1, 2, 31);
                break;

            case "Previous FY":
                start = new Date(now.getFullYear() - 1, 3, 1);
                end = new Date(now.getFullYear(), 2, 31);
                break;

            case "Custom":
            default:
                return { startDate, endDate };
        }

        return {
            startDate: start.toISOString().split("T")[0],
            endDate: end.toISOString().split("T")[0]
        };
    };

    /** API Fetch with filters */
    const fetchReportSummary = async () => {
        try {
            setLoading(true);

            let params = {};

            // Attach date filter only if NOT "All time"
            if (dateRange === "Custom") {
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            } else {
                const { startDate: s, endDate: e } = getDateRange(dateRange);
                if (s) params.startDate = s;
                if (e) params.endDate = e;
                params.preset = dateRange;
            }

            // Filter Type (All, Sales, Purchases, Payments)
            params.filterType = filterType;

            const response = await getData(
                `reports/reports/summary`,
                params,
                sessionToken
            );

            const results = response?.data || {};
            setReportSummary(results);

        } catch (error) {
            toast.error(error.message || "Failed to fetch Report Summary");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReportSummary();
    }, [dateRange, startDate, endDate, filterType]);

    const invoices = reportSummary?.invoices || {};
    const purchases = reportSummary?.purchases || {};
    const sales = reportSummary?.sales || {};
    const profit = reportSummary?.profit || 0;
    const netProfit = Number(reportSummary?.profit || 0);
    const profitAmount = netProfit > 0 ? netProfit : 0;
    const lossAmount = netProfit < 0 ? Math.abs(netProfit) : 0;

    const downloadExcel = () => {
        if (!reportSummary) return;

        const { invoices, purchases, sales, profit } = reportSummary;

        // Create JSON for Excel
        const data = [
            { Section: "Invoices", ...invoices },
            { Section: "Purchases", ...purchases },
            { Section: "Sales", ...sales },
            { Section: "Profit", total: profit }
        ];

        // Create Sheet
        const ws = XLSX.utils.json_to_sheet(data);

        // Create Workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report Summary");

        // Save
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "ReportSummary.xlsx");
    };

    return (
        <div className="summary-layout1">
            <div className="summary-content1">
                <main className="summary-main1">

                    {/* ================== FILTER BAR ================== */}
                    <div style={{ display: "flex", gap: "10px", marginBottom: "20px", alignItems: "center" }}>

                        {/* Date Range Dropdown */}
                        <TextField
                            select
                            label="Date Range"
                            value={dateRange}
                            onChange={e => {
                                setDateRange(e.target.value);
                                if (e.target.value !== "Custom") {
                                    const { startDate: s, endDate: ed } = getDateRange(e.target.value);
                                    setStartDate(s);
                                    setEndDate(ed);
                                }
                            }}
                            SelectProps={{ native: true }}
                        >
                            <option value="Today">Today</option>
                            <option value="Yesterday">Yesterday</option>
                            <option value="Last 7 Days">Last 7 Days</option>
                            <option value="Last Month">Last Month</option>
                            <option value="Current Month">Current Month</option>
                            <option value="Current FY">Current FY</option>
                            <option value="Previous FY">Previous FY</option>
                            <option value="Custom">Custom Date</option>
                        </TextField>

                        {/* Custom Dates */}
                        {dateRange === "Custom" && (
                            <>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </>
                        )}

                        {/* Report Type Filter */}
                        <TextField
                            select
                            label="Report Type"
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            SelectProps={{ native: true }}
                        >
                            <option value="All">All</option>
                            <option value="Sales">Sales Only</option>
                            <option value="Purchases">Purchases Only</option>
                            <option value="Payments">Payments Only</option>
                        </TextField>

                        {/* Export Button */}
                        <Button
                            variant="contained"
                            color="success"
                            onClick={downloadExcel}
                        >
                            Export Excel
                        </Button>
                    </div>

                    {/* ================== SUMMARY CARDS ================== */}
                    <div className="summary-cards-container">

                        {/* Purchases Card */}
                        <div className="purchases-card">
                            <div className="card-header">
                                <h4 className="heading">Purchases</h4>
                                <img className="purchase-img" src="/assets/purchase-total.png" alt="Purchases" />
                            </div>
                            <div className="report-divider"></div>
                            <div className="card-content">
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Total Amount: ₹{(purchases.totalAmount || 0).toFixed(2)}
                                </Typography>
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Paid: ₹{(purchases.paid || 0).toFixed(2)}
                                </Typography>
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Due: ₹{(purchases.due || 0).toFixed(2)}
                                </Typography>
                            </div>
                        </div>

                        {/* Sales Card */}
                        <div className="sales-card">
                            <div className="card-header">
                                <h4 className="heading">Sales</h4>
                                <img className="purchase-img" src="/assets/sales receipt.png" alt="Sales" />
                            </div>
                            <div className="report-divider"></div>
                            <div className="card-content">
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Name: {sales.name || 0}
                                </Typography>
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Quantity: {sales.qty || 0}
                                </Typography>
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Total Amount: ₹{sales.totalValue || 0}
                                </Typography>
                            </div>
                        </div>

                        {/* Payments Card */}
                        <div className="payment-card">
                            <div className="card-header">
                                <h4 className="heading">Payment</h4>
                                <img className="purchase-img" src="/assets/payment.png" alt="Payment" />
                            </div>
                            <div className="report-divider"></div>
                            <div className="card-content">
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Total Amount: ₹{(invoices.totalAmount || 0).toFixed(2)}
                                </Typography>
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Cash: ₹{(invoices.cash || 0).toFixed(2)}
                                </Typography>
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Card: ₹{(invoices.card || 0).toFixed(2)}
                                </Typography>
                                <Typography sx={{ padding: "10px", fontWeight: 500 }}>
                                    Online: ₹{(invoices.online || 0).toFixed(2)}
                                </Typography>
                            </div>
                        </div>

                        {/* Profit Card */}
                        {/* Profit Card */}
                        <div className="profit-card">
                            <div className="card-header">
                                <h4 className="heading">Profit / Loss</h4>
                                <img className="purchase-img" src="/assets/profit.png" alt="Profit" />
                            </div>
                            <div className="report-divider"></div>

                            <div className="card-content">
                                <Typography sx={{ padding: "10px", fontWeight: 500, color: "green" }}>
                                    Profit: ₹{profitAmount.toFixed(2)}
                                </Typography>

                                <Typography sx={{ padding: "10px", fontWeight: 500, color: "red" }}>
                                    Loss: ₹{lossAmount.toFixed(2)}
                                </Typography>
                            </div>
                        </div>


                    </div>
                </main>
            </div>
        </div>
    );
};

export default ReportPage;
